
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { PERMISSION } from '@/permissions';
import { alphabeticalSort } from '@/utils/sort';
import Toast from '@/utils/toast';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import CsvImportModal from '@/components/menu/CsvImportModal.vue';
import PauseOrdersModal from '@/components/order-management/PauseOrdersModal.vue';

const namespace: string = 'auth';

@Component<LocationList>({
	components: {
		CsvImportModal,
		TableHeaders,
		SuitesHeader,
		TextButton,
		IconButton,
		WarningModal,
		PauseOrdersModal
	}
})
export default class LocationList extends Vue {
	@Action('pullMenus', { namespace: 'menus' }) private pullMenus!: (payload: any) => Promise<Menu[]>;
	@Action('buildLocalMenu', { namespace: 'menus' }) private buildLocalMenu!: (restaurantId: string) => Promise<void>;
	@Action('setTenant', { namespace }) private setTenant!: (tenantId: string) => Promise<void>;
	@Getter('getTenantId', { namespace }) private tenantId!: string;
	@Getter('getRestaurants', { namespace }) private locations!: Tenant[];
	@Getter('isPosIntegrated', { namespace }) private isPosIntegrated!: boolean;
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;

	private showCsvImportModal: boolean = false;
	private showWarningModal: boolean = false;
	private selectedRestaurantId: string = '';
	private sortDirection: 'asc' | 'desc' = 'asc';
	private selectedLocation: Tenant | null = null;
	private pauseTargetLocation: Tenant | null = null;
	private PERMISSION = PERMISSION;

	// Only show pull all button if more than one integrated restaurants, otherwise it's redundant
	private get showPullAllButton(): boolean {
		return this.locations.some((restaurant: Tenant) => restaurant.pos_integrated);
	}

	private get currentEnv(): string {
		return process.env.NODE_ENV;
	}

	private get tableHeaders(): TableHeader[] {
		return [
			{ text: this.$t('settings.locations.table.headers.name'), name: 'name', sort: this.sortDirection },
			...(this.hasPermission(PERMISSION.ADMIN_ONLY) ? [
				{ text: this.$t('settings.locations.table.headers.slug') },
				{ text: this.$t('settings.locations.table.headers.group') }
			] : []),
			{ text: this.$t('settings.locations.table.headers.actions') }
		];
	}

	private get sortedLocations(): Tenant[] {
		return [...this.locations].sort((a, b) => {
			return this.sortDirection === 'asc' ? alphabeticalSort(a, b) : alphabeticalSort(b, a);
		});
	}

	private goToEnterprisePull(): void {
		this.$router.push('/locations/enterprise-pull').catch(() => {});
	}

	private async setRestaurant(tenantId: string): Promise<void> {
		await this.setTenant(tenantId);
		this.$router.replace('/menus');
	}

	/**
	 * Pull menu(s)
	 *
	 * @return {void}
	 */
	private async menuPull(restaurant: Tenant): Promise<void> {
		this.toggleMenuPullWarning();

		try {
			await this.pullMenus({ id: restaurant.id, name: restaurant.name, pullAll: false });
			Toast.success(this.$t('menu.pull.success_menu_pull'));
		}
		catch (error) {
			Toast.error(this.$t('menu.pull.error_menu_pull'));
		}
	}

	/**
	 * Show/Hide the menu pull warning modal
	 *
	 * @param {Tenant} [location]
	 * @return {void}
	 */
	private toggleMenuPullWarning(location?: Tenant): void {
		if (location) {
			this.selectedLocation = location;
			this.showWarningModal = true;
		}
		else {
			this.showWarningModal = false;
			this.selectedLocation = null;
		}
	}

	/**
	 * Open the CSV import modal and pass the restaurant ID to it
	 *
	 * @param {string} restaurantId
	 * @return {void}
	 */
	private openCsvImportModal(restaurantId: string): void {
		this.selectedRestaurantId = restaurantId;
		this.showCsvImportModal = true;
	}

	/**
	 * Toggle the sort direction for the table.
	 *
	 * @return {void}
	 */
	private toggleSort(): void {
		this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
	}

	/**
	 * Merge 'group' and 'groups' properties of a location while removing duplicates.
	 * TODO: Remove this method and just use 'groups.join(', ')' in the template once we deprecate 'group'
	 *
	 * @param {Tenant} location
	 * @return {string}
	 */
	private getLocationGroups(location: Tenant): string {
		const groups: string[] = [];

		if (location.group) {
			groups.push(location.group);
		}

		if (location.groups?.length) {
			groups.push(...location.groups);
		}

		const uniqueGroups = [...new Set(groups)];
		return uniqueGroups.join(', ');
	}
}
